import {Injectable, signal} from '@angular/core'
import {BehaviorSubject, filter, Observable} from 'rxjs'
import {
  IBorgoApplication,
  IBorgoApplicationResult,
  IBorgoLoanApplicant,
  IBorgoUserLoan,
  IPartyData,
  ISavedApplication,
  IUserDataResult
} from '@sparbanken-syd/borgo-types'
import {tap} from 'rxjs/operators'
import {verify} from '@sparbanken-syd/personnummer'

export const DEFAULT_USER: IPartyData & IUserDataResult = {
  address: {
    addressLine: [],
    location: '',
    postalCode: '',
    country: '',
    addressType: 'PRIMARY',
    addressValid: true
  },
  emails: [],
  name: '',
  personNummer: '',
  phones: [],
  partyId: ''
}

@Injectable({
  providedIn: 'root'
})
export class ApplicationService {

  /**
   * The intention of this is that "consumers"
   * shall read the application. Unfortunately
   * they read from `currentApplicationResult$`
   * I seriously must correct that!
   */
  public currentApplication$: Observable<IBorgoApplication | null>

  /**
   * Stores the current saved data, the data
   * we store in our backend. Normally null
   * in early stages.
   */
  public currentSaveData$ = new BehaviorSubject<ISavedApplication | null>(null)

  /**
   * An "Application" that contains hte "application" +
   * the surrounding metadata.
   */
  public currentApplicationResult$ = new BehaviorSubject<IBorgoApplicationResult | null>(null)

  public readonly applicationId$ = signal<string>('')

  public readonly caseId$ = signal<string>('')

  public readonly currentUser$ = signal<IPartyData & IUserDataResult>(DEFAULT_USER)

  public readonly currentUserLoans$ = signal<IBorgoUserLoan[]>([])

  public readonly coApplicant$ = new BehaviorSubject<boolean>(false)

  public readonly isPrimary$ = signal<boolean>(false)

  public isDevelopment = signal<boolean>(false)

  private readonly pCurrentApplication$ = new BehaviorSubject<IBorgoApplication | null>(null)

  constructor() {
    this.currentApplication$ = this.pCurrentApplication$.asObservable()

    this.currentApplicationResult$.pipe(
      tap(() => {
        this.applicationId$.set('')
        this.caseId$.set('')
      }),
      filter(Boolean)
    ).subscribe({
      next: a => {
        this.applicationId$.set(a.applicationId)
        this.caseId$.set(a.caseId)
        this.setPrimary(a.application.applicants)
        this.coApplicant$.next(a.application.applicants?.length > 1)
        this.pCurrentApplication$.next(a.application)
      }
    })
  }

  public reset(): void {
    this.applicationId$.set('')
    this.caseId$.set('')
    this.currentUser$.set(DEFAULT_USER)
    this.currentUserLoans$.set([])
    this.currentSaveData$.next(null)
  }

  private setPrimary(applicants: IBorgoLoanApplicant[]) {
    if (!this.currentUser$().personNummer || applicants.length <= 0) {
      return
    }
    const currentUserSub = verify(this.currentUser$().personNummer)[12]
    // Primary is the first applicant marked as primary.
    const primary = applicants
      .filter(a => a.primary)
      .map(a => a.nationalId)[0]
    // Important enough, sort the applicants so that primary is top of list.
    applicants
      .sort((a) => {
        return a.primary ? -1 : 1
      })
    const isPrimary = currentUserSub === verify(primary)[12]
    this.isPrimary$.set(isPrimary)
  }
}
